// this is the "manager" of the right-hand pane. It's the thing 
// that actually downloads the file, shows a progress bar while 
// it's downloading, shows an error message if something goes
//  wrong, and only hands the file over to a viewer once it's 
// fully downloaded and safe to open. It also has the header bar 
// (filename + download button) that stays the same no matter 
// which of the 4 file types you're looking at.



import React, { Suspense } from 'react';
import { FILE_TYPE_META, PreviewFile } from '../types';
import { useDocumentBytes } from '../hooks/useDocumentBytes';
import { usePaneWidth } from '../hooks/usePaneWidth';
// import { useSelectionCopyPopup } from '../hooks/useSelectionCopyPopup';
import { RENDERERS } from '../renderers';
import ErrorBoundary from './ErrorBoundary';
import UnsupportedNotice from './UnsupportedNotice';
// import SelectionCopyPopup from './SelectionCopyPopup';
import './DocPreviewShell.css';

interface DocPreviewShellProps {
  doc: PreviewFile;
  onClose: () => void;
  onRefreshLink: () => void;
}

// Owns everything that isn't the document itself: header, loading skeleton
// with real progress, error states, the download-from-buffer button, and
// the ErrorBoundary around the renderer. Renderers stay pure (bytes in, DOM
// out) — all of this app-facing plumbing lives here instead.
const DocPreviewShell: React.FC<DocPreviewShellProps> = ({ doc, onClose, onRefreshLink }) => {
  const { status, bytes, progress, error, retry } = useDocumentBytes(doc.sasUrl);
  const [bodyRef, { width, settledWidth }] = usePaneWidth<HTMLDivElement>();

  const handleDownload = () => {
    if (!bytes) return;
    // Blob's constructor copies the bytes it's given rather than
    // transferring them, so this is safe to call directly on the master
    // buffer — no copyBytes() needed here (contrast PdfRenderer, which does
    // hand pdf.js a buffer it transfers).
    const blob = new Blob([bytes], { type: doc.contentType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const Renderer = RENDERERS[doc.type];

  // "Copy to chat" from a text selection —
  // only offered for PDF/DOCX, where the rendered content is real
  // // selectable text (pdf.js's text layer, docx-preview's actual HTML).
  // // XLSX/CSV are excluded: SheetGrid only renders currently-visible cells
  // // (that's the whole point of virtualizing a 50k-row sheet), so "select
  // // this" could only ever mean "select what happens to be on screen right
  // // now" — a confusing, partial feature not worth shipping.
  // const isSelectionCopyEnabled = (doc.type === 'pdf' || doc.type === 'docx') && status === 'ready';
  // const { selection, clear: clearSelectionPopup } = useSelectionCopyPopup(bodyRef, isSelectionCopyEnabled);
  // const handleCopyToChat = () => {
  //   if (!selection) return;
  //   window.dispatchEvent(new CustomEvent('chat:insert_text', { detail: { text: selection.text } }));
  //   clearSelectionPopup();
  //   window.getSelection()?.removeAllRanges();
  // };

  return (
    <div className="doc-preview-shell">
      <div className="doc-preview-header">
        <div className="doc-preview-header-title">
          <i className={FILE_TYPE_META[doc.type].icon} style={{ color: FILE_TYPE_META[doc.type].color }} aria-hidden="true" />
          <span title={doc.fileName}>{doc.fileName}</span>
        </div>
        <div className="doc-preview-header-actions">
          <button
            type="button"
            className="doc-preview-icon-btn"
            onClick={handleDownload}
            disabled={!bytes}
            aria-label="Download file"
            title="Download"
          >
            <i className="fas fa-download" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="doc-preview-icon-btn"
            onClick={onClose}
            aria-label="Close preview"
            title="Close"
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="doc-preview-body" ref={bodyRef}>
        {status === 'loading' && (
          <div className="doc-preview-loading">
            <div className="doc-preview-skeleton" />
            <span className="doc-preview-loading__label">
              {progress !== null ? `Loading… ${progress}%` : 'Loading…'}
            </span>
          </div>
        )}

        {status === 'error' && error && (
          <UnsupportedNotice
            icon={error.kind === 'expired-sas' ? 'fas fa-clock-rotate-left' : 'fas fa-triangle-exclamation'}
            title={error.kind === 'expired-sas' ? 'Link expired' : 'Failed to load file'}
            description={error.message}
            // retry() re-fetches this SAME url, which does nothing for an
            // expired SAS — onRefreshLink instead replays the original
            // request through useDocPreview (force:true), which gets a
            // brand-new SAS URL and, via doc.sasUrl changing, triggers a
            // fresh byte fetch here automatically.
            onRetry={error.kind === 'expired-sas' ? onRefreshLink : retry}
          />
        )}

        {status === 'ready' && bytes && (
          <ErrorBoundary
            // Keyed on path, not type:fileName — two different files can
            // share a type and filename (e.g. the backend generating
            // "report.pdf" twice for two different requests), which would
            // leave a previous file's crash state showing over an unrelated
            // new one that never actually failed. path is guaranteed unique
            // (unlike file_id, which can be null on history rows).
            key={doc.path}
            fallback={() => (
              <UnsupportedNotice
                icon="fas fa-bug"
                title="This file couldn't be rendered"
                description="The parser hit something it couldn't handle. You can still download the original file."
                onDownload={handleDownload}
              />
            )}
          >
            <Suspense fallback={<div className="doc-preview-loading"><div className="doc-preview-skeleton" /></div>}>
              <Renderer
                bytes={bytes}
                meta={{ fileName: doc.fileName, contentType: doc.contentType }}
                width={width}
                settledWidth={settledWidth}
              />
            </Suspense>
          </ErrorBoundary>
        )}
      </div>

      {/* {isSelectionCopyEnabled && (
        <SelectionCopyPopup selection={selection} onCopyToChat={handleCopyToChat} />
      )} */}
    </div>
  );
};

export default DocPreviewShell;
